import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Bell, Send, CheckCircle, XCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { sendPushNotification, getNotificationHistory } from "@/services/pushService";

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString("es", {
    day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
  });
}

export default function NotificationsPage() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);

  const { data: history } = useQuery({
    queryKey: ["push-history"],
    queryFn: getNotificationHistory,
  });

  const onSend = async () => {
    if (!title.trim() || !body.trim()) {
      toast({ title: "Campos requeridos", description: "Escribe un título y mensaje.", variant: "destructive" });
      return;
    }
    try {
      setSending(true);
      const result = await sendPushNotification({ title, body });
      toast({
        title: "Notificación enviada",
        description: `${result.sent} enviada(s), ${result.failed} fallida(s).`,
      });
      setTitle("");
      setBody("");
      queryClient.invalidateQueries({ queryKey: ["push-history"] });
    } catch (e) {
      toast({ title: "Error", description: e instanceof Error ? e.message : "No se pudo enviar.", variant: "destructive" });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <h1 className="text-2xl font-heading font-bold text-foreground">Notificaciones Push</h1>
      <p className="text-sm text-muted-foreground">Envía notificaciones a los usuarios de la app móvil.</p>

      <Card>
        <CardHeader><CardTitle className="text-sm font-heading">Enviar notificación</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Título</Label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Nuevo audiolibro disponible"
              maxLength={100}
            />
          </div>
          <div className="space-y-2">
            <Label>Mensaje</Label>
            <Textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Descubre nuestro nuevo título..."
              rows={3}
              maxLength={500}
            />
            <p className="text-xs text-muted-foreground">{body.length}/500 caracteres</p>
          </div>
          <Button onClick={onSend} disabled={sending}>
            {sending ? "Enviando..." : <><Send className="h-4 w-4 mr-2" />Enviar a todos los usuarios</>}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-sm font-heading">Historial de envíos</CardTitle></CardHeader>
        <CardContent>
          {!history?.length ? (
            <div className="text-center py-8 text-muted-foreground">
              <Bell className="h-12 w-12 mx-auto mb-4 opacity-30" />
              <p className="font-heading font-semibold mb-1">Sin notificaciones enviadas</p>
              <p className="text-sm">Las notificaciones que envíes aparecerán aquí.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Título</TableHead>
                  <TableHead>Mensaje</TableHead>
                  <TableHead>Destino</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead>Enviados</TableHead>
                  <TableHead>Fecha</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {history.map((n: any) => (
                  <TableRow key={n.id}>
                    <TableCell className="font-medium text-sm">{n.title}</TableCell>
                    <TableCell className="text-sm text-muted-foreground max-w-[200px] truncate">{n.body}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs">
                        {n.target === "all" ? "Todos" : "Usuario"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {n.status === "sent" ? (
                        <Badge className="bg-primary/10 text-primary text-xs"><CheckCircle className="h-3 w-3 mr-1" />Enviado</Badge>
                      ) : (
                        <Badge variant="destructive" className="text-xs"><XCircle className="h-3 w-3 mr-1" />Fallido</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">{n.sent_count}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{formatDate(n.created_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
